/**
 * The traveller's OWN timezone, as their browser reports it.
 *
 * ── What this is NOT ────────────────────────────────────────────────────────
 * Not the DESTINATION timezone. That one is resolved from the hotel/activity
 * coordinates through the Google Timezone API — see `utils/timezoneUtils.ts`,
 * `fetchTimezoneFromCoords`. The two are normally different and are used for
 * different things: the destination zone decides which calendar day a date
 * picker may not go below, this one describes the person doing the booking.
 *
 * ── Trust ───────────────────────────────────────────────────────────────────
 * This is whatever the user's operating system clock is set to. It is fine for
 * display, formatting, notification scheduling and audit; it is NOT an identity,
 * a location, or anything that may feed pricing or entitlement decisions. A
 * traveller in Mumbai with their laptop set to New York is a supported case, not
 * an error to correct.
 *
 * ── Transport ───────────────────────────────────────────────────────────────
 * Sent BOTH ways (see the matching app-backend helper
 * `src/utils/helpers/userTimezone.ts`):
 *
 *   header  x-user-timezone: Asia/Kolkata     — readable at `onRequest` time
 *   body    { "userTimezone": "Asia/Kolkata" } — readable after the AES decrypt
 *
 * app-backend only populates `request.body` from the `preValidation` decrypt
 * onwards (src/app/encrpt.ts), so an `onRequest` hook can see the header and
 * nothing else. The body field is what survives a proxy that drops unknown
 * headers, and the half that needs no CORS allow-listing at all. Headers win
 * when both are present.
 */

/**
 * IANA zone names, canonical and legacy.
 *
 * Deliberately tolerant of a name with NO slash: `UTC` and `GMT` are what
 * browsers fall back to when the host has no zone database, and rejecting them
 * would drop the field for exactly the users whose clock we know least about.
 * Up to three segments, because `America/Argentina/Buenos_Aires` and
 * `America/Indiana/Indianapolis` are real. `+`/`-` are allowed for `Etc/GMT+5`.
 *
 * Verified against the full tzdata set (599 names including every legacy Link):
 * zero rejections, and the longest real name is 30 characters.
 */
const IANA_NAME = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+){0,2}$/;

const MAX_LENGTH = 64;

/**
 * The CLDR/ICU sentinel for "this host's zone could not be determined".
 *
 * Shaped like a real zone and so accepted by the regex, but it is NOT in tzdata:
 * `new Intl.DateTimeFormat("en", { timeZone: "Etc/Unknown" })` throws RangeError,
 * and so does `dayjs.tz(t, "Etc/Unknown")`. Reproducible with `TZ=Factory`.
 *
 * Rejected here rather than passed on, because it means precisely "we don't
 * know" — which this module expresses by omitting the field, not by sending a
 * value that makes every downstream consumer throw.
 */
const UNRESOLVED = "Etc/Unknown";

/**
 * ── GATEWAY PREREQUISITE — read before promoting this to a new environment ──
 *
 * `x-user-timezone` is a CUSTOM request header, and a custom header is only
 * sent if the CORS preflight names it in `Access-Control-Allow-Headers`. In the
 * deployed environments that response comes from API GATEWAY, not app-backend,
 * and its list is FIXED — it does not echo what the browser asks for.
 *
 * So an environment whose gateway does not name it does not degrade, it FAILS
 * ENTIRELY: the browser refuses the request and every call errors, including
 * ones with nothing to do with this feature. That is not hypothetical — it
 * happened on 2026-09-08, when this shipped unflagged before the gateway edit.
 *
 * This was behind REACT_APP_SEND_USER_TIMEZONE_HEADER until 2026-09-09. The
 * flag is GONE now because the three environments this branch reaches have all
 * been patched:
 *
 *   api-devcrm  ALLOWED      api-dev  ALLOWED      api-test  ALLOWED
 *
 * ⚠️ PAT AND PRODUCTION HAVE NOT BEEN PATCHED. Measured 2026-09-09:
 *
 *   api-pat.zenvoya.ai  NOT allowed
 *   api.zenvoya.ai      NOT allowed
 *
 * There is no flag left to protect them. Before this code is promoted to
 * release/pat or release/production, add `X-User-Timezone` to that
 * environment's gateway allow-list and confirm it:
 *
 *   curl -s -X OPTIONS "https://<api-host>/app/v1/itinerary/booking" \
 *     -H "Origin: https://<app-host>" \
 *     -H "Access-Control-Request-Method: POST" \
 *     -H "Access-Control-Request-Headers: x-user-timezone" \
 *     -D- -o/dev/null | grep -i access-control-allow-headers
 *
 * The BODY field (`getUserTimezoneField`) is unaffected by any of this — it
 * needs no gateway change and has always shipped.
 */

/**
 * Resolved once per page load.
 *
 * `undefined` = not yet computed; `null` = computed and unavailable. A zone can
 * only change mid-session if the user edits their OS clock, which is not worth
 * an `Intl` construction on every single request.
 */
let cached: string | null | undefined;

/**
 * The browser's IANA timezone name, or `undefined` when it cannot be trusted.
 *
 * Never returns `""` or `null` — every caller spreads this into a payload or a
 * header, and an empty value is worse than an absent one: the backend field is
 * OPTIONAL precisely so that "we don't know" can be expressed by omission.
 */
export const getUserTimezone = (): string | undefined => {
  if (cached !== undefined) return cached ?? undefined;

  cached = (() => {
    try {
      // Guarded rather than assumed: `Intl` is absent in some embedded
      // webviews, and `resolvedOptions().timeZone` is itself specified as
      // optional — a few older Safari builds return undefined here. The bare
      // `Intl` reference can also throw ReferenceError, which is why the whole
      // expression sits inside this try rather than relying on `?.` alone.
      const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (typeof zone !== "string") return null;

      const trimmed = zone.trim();
      if (!trimmed || trimmed.length > MAX_LENGTH) return null;
      if (trimmed === UNRESOLVED) return null;
      if (!IANA_NAME.test(trimmed)) return null;

      return trimmed;
    } catch {
      return null;
    }
  })();

  return cached ?? undefined;
};

/**
 * The `x-user-timezone` header, spread-ready — `{}` when the browser reports no
 * usable zone. Sent unconditionally otherwise; see the gateway note above.
 *
 * Spread into the header literals in `utils/api/encryption.ts getHeaderConfig`
 * and `utils/apiFunction.ts fetchApi`, which between them are every app-backend
 * call this branch makes. (The b2b2c branch does this in a single axios
 * interceptor instead; that module does not exist here, and adding one just for
 * this would be a larger change than the two spreads.)
 */
export const getUserTimezoneHeader = (): { "x-user-timezone"?: string } => {
  const zone = getUserTimezone();
  return zone ? { "x-user-timezone": zone } : {};
};

/**
 * The `userTimezone` body field, spread-ready.
 *
 * Returns `{}` — the field omitted entirely — when the zone is unavailable,
 * mirroring `getRequestedItineraryIdField()`. Never sends an empty string: the
 * booking stack has a history of rejecting empty-but-present fields (see the
 * `id must match format "uuid"` lesson in `booking/requestedItineraryId.ts`).
 *
 * UNGATED, unlike the header. A body field cannot cause the CORS outage the
 * header can, and app-backend's booking schemas do not set
 * `additionalProperties: false`, so this is accepted whether or not the backend
 * half has shipped yet.
 */
export const getUserTimezoneField = (): { userTimezone?: string } => {
  const zone = getUserTimezone();
  return zone ? { userTimezone: zone } : {};
};
